import {
  GROUP_SECTIONS,
  groupMap,
  type SettingsGroupSection,
  type SettingsSectionId,
} from "../admin/settings-groups.ts";
import { SETTINGS_GROUPS, type SettingsGroup } from "../router.tsx";

/**
 * Instance policies edited on a page of their own, never on the policies pages: `models.providers`
 * (model-picking D1) is the Model connections page's Providers section; the labs, effort
 * families, lab providers and reasoning per family (D4, D5, D15) get the Models screen's
 * sections (model-picking slice 5), and are set through the API until then.
 */
export const OWN_PAGE_POLICIES: readonly string[] = [
  "models.providers",
  "models.labs",
  "models.effortFamilies",
  "models.labProvider",
  "models.effortReasoning",
  // Retired by Q19 (model-picking slice 5b): kept off the policies pages until it is dropped.
  "models.familyReasoning",
  // Pins (model-picking D7) get their control with the Models screen (slice 5); the probe
  // interval has its own control on the Model connections page.
  "models.versionPin",
  "models.healthProbeInterval",
];

/** A section of a policies page: the backoffice's. */
export type PolicySectionId = SettingsSectionId;

/**
 * Instance policies the backoffice settings do not show: their page and section on the
 * console (artefacts with answers and conversations) and their words
 * (`console.policies.keys.<copy>`). Kept here so tenant admins never see them (the backoffice
 * map and copy are unchanged).
 */
export const POLICY_EXTRAS = {
  "conversations.artefacts": { group: "general", section: "answers", copy: "artefacts" },
  // The triage assistant's daily spend (feedback-triage-assist D5): instance only.
  "feedback.triageAlertUsd": { group: "security", section: "esiTeam", copy: "triageAlert" },
  "feedback.triageDailyCapUsd": { group: "security", section: "esiTeam", copy: "triageCap" },
} as const satisfies Readonly<
  Record<string, { group: SettingsGroup; section: PolicySectionId; copy: string }>
>;

/** The backoffice's group sections with the console's extras added in place. */
const POLICY_SECTIONS = Object.fromEntries(
  SETTINGS_GROUPS.map((group) => {
    const sections: { id: PolicySectionId; keys: string[] }[] = GROUP_SECTIONS[group].map(
      (s: SettingsGroupSection) => ({ id: s.id, keys: [...s.keys] }),
    );
    for (const [key, extra] of Object.entries(POLICY_EXTRAS)) {
      if (extra.group !== group) {
        continue;
      }
      const section = sections.find((s) => s.id === extra.section);
      if (section) {
        // Once: a key the backoffice already places there keeps its place.
        if (!section.keys.includes(key)) {
          section.keys.push(key);
        }
      } else {
        sections.push({ id: extra.section, keys: [key] });
      }
    }
    return [group, sections];
  }),
) as Record<SettingsGroup, { id: PolicySectionId; keys: string[] }[]>;

const POLICIES = groupMap(POLICY_SECTIONS);

/** The policies page a key is on (a key no group names lands on General, under "Other"). */
export const policyGroupOf = POLICIES.groupOf;
export const policySectionOf = POLICIES.sectionOf;
export const policySectionsOf = POLICIES.sectionsOf;

/** The groups holding at least one of `policies`, in the settings order. */
export const policyGroups = (policies: readonly { key: string }[]): SettingsGroup[] =>
  SETTINGS_GROUPS.filter((g) => policies.some((p) => policyGroupOf(p.key) === g));
